import { Data, Effect } from 'effect'

// executeAggregationQueryEffect 系の失敗チャンネルを構成するタグ付きエラー群。
//
// 失敗経路 (SQL実行 / SQL生成 / sqlOverrideResolver) ごとにタグを分けることで、呼び出し元が
// 「拾う失敗の範囲」を Effect.catchTag で型検査可能な形で表現できるようにする
// (aggregation単体は全タグを正常レスポンス化する一方、multiTablePivot は SqlExecutionError のみ
//  KPI単位のエラー表示に変換 (正常化) し、他タグは全体の例外として素通しする非対称な契約がある)。
//
// いずれも throw 元のエラーを cause に保持する。呼び出し元の instanceof / message 判定の外部契約を
// 壊さないよう、現時点ではアプリ境界 (Promise 変換箇所・レスポンス組み立て箇所) で cause を取り出して
// 従来の Error / message 形式に変換するため、Bugsnag に届くのは元 Error そのもの (cause 保持の効能は
// まだ発揮されない)。#9426 でタグ付きエラーを境界の外まで運ぶようになった時点で、Bugsnag が
// Error.cause chain を自動展開するため、元 stack を辿れる形のままタグの構造化情報を載せられる。

/** SQL実行 (sqlClient.query) の失敗。エラー時レスポンスの組み立てに必要なSQL情報を持つ */
export class SqlExecutionError extends Data.TaggedError('SqlExecutionError')<{
  cause: Error
  sql: string
  originalRawSql: string
  overriddenRawSql: string | undefined
}> {}

/** SQL生成 (generateAggregationSql / renderSql) の失敗 */
export class SqlGenerationError extends Data.TaggedError('SqlGenerationError')<{ cause: Error }> {}

/** sqlOverrideResolver の失敗 */
export class SqlOverrideResolverError extends Data.TaggedError('SqlOverrideResolverError')<{ cause: Error }> {}

export type AggregationQueryError = SqlExecutionError | SqlGenerationError | SqlOverrideResolverError

/**
 * Effect.catch に渡すハンドラを作る。throw された値のうち Error は wrapError でタグ付きエラーに
 * 包んで失敗チャンネルへ移し、Error 以外 (文字列 throw 等) は分類不能な欠陥として defect にする。
 * defect は Effect.runPromise の境界で throw 元の値のまま reject されるため、
 * 「Error 以外の throw はどの層でも変換されず呼び出し元へ素通しされる」従来契約が維持される。
 *
 * `Effect.try*({ catch: identity })` で一旦 unknown のまま失敗チャンネルに載せ、直後の
 * `Effect.catch(wrapErrorOrDie(...))` で fail / die に振り分けるので、両者は常にセットで使うこと
 * (全部包むと Error 以外も catchTag に捕まり素通し契約が壊れ、包まず流すと E に unknown が残る)。
 *
 * 注意: `catch: identity` を冗長とみなして catch を省いた `Effect.try(fn)` の形に書き換えてはいけない。
 * tsc は通るが、catch なしの形は throw された値を `Cause.UnknownError` に包み、これは Error のサブクラス
 * なので本ヘルパーが fail に振ってしまい、「Error 以外の throw は素通し」の契約が型エラーなしに壊れる。
 */
export const wrapErrorOrDie =
  <E>(wrapError: (cause: Error) => E) =>
  (thrown: unknown): Effect.Effect<never, E> =>
    thrown instanceof Error ? Effect.fail(wrapError(thrown)) : Effect.die(thrown)

/* eslint-disable sonarjs/todo-tag */
/**
 * エラーの message を `${name}: ${message}` 形式に書き換えて同一インスタンスを返す。
 *
 * 旧 ExecuteViewPostgresError ラッパーが `${e.name}: ${e.message}` で組み立てていた message 形式を、
 * ラップ層なしで維持するための一時措置。フロントの viewUIErrorUtil.ts が
 * `message.includes('Error: SQLの実行中にエラー')` の文字列マッチで表示を分岐しており、
 * name プレフィックスが消えると SQL エラーの案内表示が壊れるため、フロントが構造化情報
 * (errorInfo) ベースの判定に移行するまでこの形式を保つ。
 *
 * 新しい Error を生成しないのは、throw 元の stack trace と cause chain をそのまま保持するため。
 * 冪等ではない (2 回適用すると name が二重に付く) ので、sqlClient.query の catch 直下でのみ呼ぶこと。
 *
 * TODO: epic &138 の #9435 (フロント文字列判定の撤去) 完了後、本ヘルパーと呼び出し元 3 箇所
 * (list / withApplicationJoin / aggregation) を削除する。設計判断は epic &131 / issue #9436 を参照。
 */
/* eslint-enable sonarjs/todo-tag */
export const prefixErrorNameToMessage = (error: Error): Error => {
  // eslint-disable-next-line no-param-reassign -- stack / cause chain を保持するため新 Error を作らず意図的に mutate する (上記 JSDoc 参照)
  error.message = `${error.name}: ${error.message}`
  return error
}
